import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextareaAutosize
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Clear,
  Save,
  Cancel,
  FilterList
} from '@mui/icons-material';

const Matieres = () => {
  const [data, setData] = useState([]);
  const [modules, setModules] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [filiereNiveaux, setFiliereNiveaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [filtreAnnee, setFiltreAnnee] = useState('');
  const [filtreFiliere, setFiltreFiliere] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtrePeriode, setFiltrePeriode] = useState('');
  const [filtreModule, setFiltreModule] = useState('');
  
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    nom: '',
    module_id: '',
    periode_id: '',
    niveau_id: '',
    filiere_id: '',
    annee_universitaire_id: '',
    enseignant_id: '',
    credits: 0,
    coefficient: 1,
    volumeHoraire: { cours: 0, td: 0, tp: 0 },
    description: '',
    estActif: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matieresRes, modulesRes, periodesRes, niveauxRes, filieresRes, anneesRes, fnRes] = await Promise.all([
        axios.get('/api/matieres', { headers: getAuthHeaders() }),
        axios.get('/api/modules', { headers: getAuthHeaders() }),
        axios.get('/api/periodes', { headers: getAuthHeaders() }),
        axios.get('/api/niveaux', { headers: getAuthHeaders() }),
        axios.get('/api/filieres', { headers: getAuthHeaders() }),
        axios.get('/api/annees', { headers: getAuthHeaders() }),
        axios.get('/api/filiere-niveaux', { headers: getAuthHeaders() })
      ]);
      setData(matieresRes.data || []);
      setModules(modulesRes.data || []);
      setPeriodes(periodesRes.data || []);
      setNiveaux(niveauxRes.data || []);
      setFilieres(filieresRes.data || []);
      setAnnees(anneesRes.data || []);
      setFiliereNiveaux(fnRes.data || []);
      setError('');
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  // Les identifiants viennent de Mongo et circulent en String d un cote, ObjectId de l autre.
  // Une comparaison stricte echouait selon le type, et la matiere etait alors affichee sans
  // periode, sans niveau ni sans annee. On compare donc toujours en chaine.
  const memeId = (a, b) => a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

  // Les periodes sont dupliquees en base : sans deduplication le Select affiche deux fois
  // « Semestre 5 » et React leve une erreur de cles dupliquees.
  const sansDoublonParId = (liste) => {
    const vus = new Set();
    return liste.filter((item) => {
      const cle = String(item._id);
      if (vus.has(cle)) return false;
      vus.add(cle);
      return true;
    });
  };

  const getModule = (id) => modules.find(m => memeId(m._id, id));
  const getPeriode = (id) => periodes.find(p => memeId(p._id, id));
  const getNiveau = (id) => niveaux.find(n => memeId(n.niveau_id, id) || memeId(n._id, id));
  const getFiliere = (id) => filieres.find(f => memeId(f.filiere_id, id) || memeId(f._id, id));
  const getAnnee = (id) => annees.find(a => memeId(a._id, id) || memeId(a.annee_id, id));

  // Helper to get all valid ID formats for a given Annee value
  const getAnneeIds = (value) => {
    if (!value) return [];
    const annee = annees.find(a => (a.annee_id || a._id) === value);
    return annee ? [value, String(annee._id), annee.annee_id].filter(Boolean) : [value];
  };

  // Cascade pour les filtres
  const filteredFilieres = filtreAnnee ? filieres.filter(f => getAnneeIds(filtreAnnee).includes(f.annee_id)) : filieres;
  const filteredNiveaux = filtreFiliere
    ? niveaux.filter(n => filiereNiveaux.some(fn => memeId(fn.niveau_id, n.niveau_id) && memeId(fn.filiere_id, filtreFiliere)))
    : (filtreAnnee
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              memeId(fn.niveau_id, n.niveau_id) &&
              filieres.some(f => memeId(f.filiere_id, fn.filiere_id) && getAnneeIds(filtreAnnee).includes(f.annee_id))
            )
          )
        : niveaux);
  const filteredPeriodes = filtreNiveau ? sansDoublonParId(periodes.filter(p => memeId(p.niveau_id, filtreNiveau))) : periodes;
  const filteredModules = filtrePeriode ? modules.filter(m => memeId(m.periode_id, filtrePeriode)) : modules;

  // Cascade pour le formulaire
  const formFilteredFilieres = formData.annee_universitaire_id
    ? filieres.filter(f => getAnneeIds(formData.annee_universitaire_id).includes(f.annee_id))
    : filieres;
  const formFilteredNiveaux = formData.filiere_id
    ? niveaux.filter(n => filiereNiveaux.some(fn => memeId(fn.niveau_id, n.niveau_id) && memeId(fn.filiere_id, formData.filiere_id)))
    : (formData.annee_universitaire_id
        ? niveaux.filter(n =>
            filiereNiveaux.some(fn =>
              memeId(fn.niveau_id, n.niveau_id) &&
              filieres.some(f => memeId(f.filiere_id, fn.filiere_id) && getAnneeIds(formData.annee_universitaire_id).includes(f.annee_id))
            )
          )
        : niveaux);
  const formFilteredPeriodes = formData.niveau_id
    ? sansDoublonParId(periodes.filter(p => memeId(p.niveau_id, formData.niveau_id)))
    : periodes;
  // La liste des modules NE DOIT PAS dependre de la seule periode. Elle etait filtree ainsi,
  // alors que l annee, la filiere et le niveau etaient deja choisis : on pouvait donc rattacher
  // une matiere au module d une autre filiere ou d une autre annee. La matiere etait enregistree
  // sans erreur, puis invisible dans l emploi du temps, qui ne propose que les matieres des
  // modules du niveau du groupe. Pour une nouvelle filiere, c est le piege le plus facile a
  // franchir sans s en apercevoir.
  const formFilteredModules = modules.filter(m => {
    if (formData.annee_universitaire_id && !memeId(m.annee_universitaire_id, formData.annee_universitaire_id)) return false;
    if (formData.filiere_id && !memeId(m.filiere_id, formData.filiere_id)) return false;
    if (formData.niveau_id && !memeId(m.niveau_id, formData.niveau_id)) return false;
    if (formData.periode_id && !memeId(m.periode_id, formData.periode_id)) return false;
    return true;
  });

  const filteredData = data.filter(item => {
    const search = searchTerm.toLowerCase();
    const module = getModule(item.module_id);
    const periode = getPeriode(item.periode_id);
    const niveau = getNiveau(item.niveau_id);

    if (filtreAnnee && !memeId(item.annee_universitaire_id, filtreAnnee)) return false;
    if (filtreFiliere && !memeId(item.filiere_id, filtreFiliere)) return false;
    if (filtreNiveau && !memeId(item.niveau_id, filtreNiveau)) return false;
    if (filtrePeriode && !memeId(item.periode_id, filtrePeriode)) return false;
    if (filtreModule && !memeId(item.module_id, filtreModule)) return false;

    return (item.nom || '').toLowerCase().includes(search) ||
           (item.code || '').toLowerCase().includes(search) ||
           (module?.nom || '').toLowerCase().includes(search) ||
           (periode?.nom || '').toLowerCase().includes(search) ||
           (niveau?.intitule || '').toLowerCase().includes(search);
  });

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    if (item) {
      setSelectedItem(item);
      setFormData({
        code: item.code || '',
        nom: item.nom || '',
        module_id: item.module_id || '',
        periode_id: item.periode_id || '',
        niveau_id: item.niveau_id || '',
        filiere_id: item.filiere_id || '',
        annee_universitaire_id: item.annee_universitaire_id || '',
        enseignant_id: item.enseignant_id || '',
        credits: item.credits || 0,
        coefficient: item.coefficient || 1,
        volumeHoraire: item.volumeHoraire || { cours: 0, td: 0, tp: 0 },
        description: item.description || '',
        estActif: item.estActif !== undefined ? item.estActif : true
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      code: '',
      nom: '',
      module_id: '',
      periode_id: '',
      niveau_id: '',
      filiere_id: '',
      annee_universitaire_id: '',
      enseignant_id: '',
      credits: 0,
      coefficient: 1,
      volumeHoraire: { cours: 0, td: 0, tp: 0 },
      description: '',
      estActif: true
    });
  };

  // Une matiere herite de son module. Si les quatre champs divergent du module, l emploi du
  // temps ne la proposera jamais : il ne liste que les matieres des modules du niveau du groupe,
  // pour l annee active. mieux vaut refuser l enregistrement et nommer le champ fautif, sinon la
  // matiere disparait sans que rien ne le signale.
  const ecartsAvecModule = (donnees) => {
    const mod = getModule(donnees.module_id);
    if (!mod) return [];
    const ecarts = [];
    if (donnees.annee_universitaire_id && mod.annee_universitaire_id && !memeId(donnees.annee_universitaire_id, mod.annee_universitaire_id)) ecarts.push('année universitaire');
    if (donnees.filiere_id && mod.filiere_id && !memeId(donnees.filiere_id, mod.filiere_id)) ecarts.push('filière');
    if (donnees.niveau_id && mod.niveau_id && !memeId(donnees.niveau_id, mod.niveau_id)) ecarts.push('niveau');
    if (donnees.periode_id && mod.periode_id && !memeId(donnees.periode_id, mod.periode_id)) ecarts.push('période');
    return ecarts;
  };

  const handleSubmit = async () => {
    try {
      const ecarts = ecartsAvecModule(formData);
      if (ecarts.length > 0) {
        const Several = ecarts.length > 1;
        setError(`${Several ? 'Ces champs ne correspondent pas au module choisi : ' : 'Ce champ ne correspond pas au module choisi : '}${ecarts.join(', ')}. Reprenez le module pour aligner les valeurs, sinon cette matière n'apparaîtra dans aucun emploi du temps.`);
        return;
      }
      if (modalMode === 'add') {
        await axios.post('/api/matieres', formData, { headers: getAuthHeaders() });
        setSuccessMessage('Matière ajoutée avec succès');
      } else {
        await axios.put(`/api/matieres/${selectedItem._id}`, formData, { headers: getAuthHeaders() });
        setSuccessMessage('Matière modifiée avec succès');
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'opération');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer définitivement ${item.nom} ?`)) return;
    try {
      await axios.delete(`/api/matieres/${item._id}`, { headers: getAuthHeaders() });
      setSuccessMessage('Matière supprimée avec succès');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>
            📖 Gestion des Matières
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredData.length} matière(s) trouvée(s)
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal('add')}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Nouvelle Matière
          </Button>
        </Stack>
      </Box>

      {/* Filtres en cascade */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="action" />
            <Typography variant="body2" fontWeight="500" color="text.secondary">Filtres :</Typography>
          </Box>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Année</InputLabel>
            <Select value={filtreAnnee} label="Année" onChange={(e) => { setFiltreAnnee(e.target.value); setFiltreFiliere(''); setFiltreNiveau(''); setFiltrePeriode(''); setFiltreModule(''); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {annees.map((a) => (<MenuItem key={a._id || a.annee_id} value={a.annee_id || a._id}>{a.intitule} {a.estActive ? '⭐' : ''}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtreAnnee}>
            <InputLabel>Filière</InputLabel>
            <Select value={filtreFiliere} label="Filière" onChange={(e) => { setFiltreFiliere(e.target.value); setFiltreNiveau(''); setFiltrePeriode(''); setFiltreModule(''); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {filteredFilieres.map((f) => (<MenuItem key={f.filiere_id} value={f.filiere_id}>{f.code_filiere || f.libelle} - {f.intitule}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtreFiliere}>
            <InputLabel>Niveau</InputLabel>
            <Select value={filtreNiveau} label="Niveau" onChange={(e) => { setFiltreNiveau(e.target.value); setFiltrePeriode(''); setFiltreModule(''); setPage(0); }}>
              <MenuItem value="">Tous</MenuItem>
              {filteredNiveaux.map((n) => (<MenuItem key={n.niveau_id} value={n.niveau_id}>{n.intitule}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtreNiveau}>
            <InputLabel>Période</InputLabel>
            <Select value={filtrePeriode} label="Période" onChange={(e) => { setFiltrePeriode(e.target.value); setFiltreModule(''); setPage(0); }}>
              <MenuItem value="">Toutes</MenuItem>
              {filteredPeriodes.map((p) => (<MenuItem key={p._id} value={p._id}>{p.nom}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 160 }} size="small" disabled={!filtrePeriode}>
            <InputLabel>Module</InputLabel>
            <Select value={filtreModule} label="Module" onChange={(e) => { setFiltreModule(e.target.value); setPage(0); }}>
              <MenuItem value="">Tous</MenuItem>
              {filteredModules.map((m) => (<MenuItem key={m._id} value={m._id}>{m.nom}</MenuItem>))}
            </Select>
          </FormControl>
          {(filtreAnnee || filtreFiliere || filtreNiveau || filtrePeriode || filtreModule) && (
            <Button size="small" variant="outlined" onClick={() => { setFiltreAnnee(''); setFiltreFiliere(''); setFiltreNiveau(''); setFiltrePeriode(''); setFiltreModule(''); }} startIcon={<Clear />}>Effacer</Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par nom, code, module, période ou niveau..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Période</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Niveau</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Filière</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Année</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Crédits</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Volume Horaire</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
              const module = getModule(item.module_id);
              const periode = getPeriode(item.periode_id);
              const niveau = getNiveau(item.niveau_id);
              const filiere = getFiliere(item.filiere_id);
              const annee = getAnnee(item.annee_universitaire_id);
              const vh = item.volumeHoraire || {};
              return (
                <TableRow key={item._id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#667eea', fontFamily: 'monospace' }}>{item.code}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{item.nom}</TableCell>
                  <TableCell>
                    <Chip label={module?.nom || '-'} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={periode?.nom || '-'} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={niveau?.intitule || '-'} size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={filiere?.code_filiere || filiere?.libelle || filiere?.intitule || '-'} size="small" sx={{ bgcolor: '#e0f2fe', color: '#0369a1' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={annee?.intitule || '-'} size="small" sx={{ bgcolor: '#ccfbf1', color: '#0f766e' }} />
                  </TableCell>
                  <TableCell>{item.credits}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {vh.cours > 0 && <Chip label={`C:${vh.cours}`} size="small" sx={{ bgcolor: '#dcfce7', color: '#166534' }} />}
                      {vh.td > 0 && <Chip label={`TD:${vh.td}`} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />}
                      {vh.tp > 0 && <Chip label={`TP:${vh.tp}`} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b' }} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.estActif ? '✅ Actif' : '❌ Inactif'}
                      size="small"
                      sx={{ bgcolor: item.estActif ? '#dcfce7' : '#fee2e2', color: item.estActif ? '#166534' : '#991b1b' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Modifier">
                        <IconButton size="small" color="warning" onClick={() => handleOpenModal('edit', item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Aucune matière trouvée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        labelRowsPerPage="Lignes par page"
        sx={{ mt: 2 }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? '➕ Ajouter une matière' : '✏️ Modifier la matière'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code *"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  margin="normal"
                  required
                />
              </Grid>
              {/* Cascade Année → Filière → Niveau → Période → Module */}
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Année Universitaire *</InputLabel>
                  <Select
                    value={formData.annee_universitaire_id}
                    onChange={(e) => setFormData({ ...formData, annee_universitaire_id: e.target.value, filiere_id: '', niveau_id: '', periode_id: '', module_id: '' })}
                    label="Année Universitaire *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {annees.map((a) => (
                      <MenuItem key={a._id || a.annee_id} value={a.annee_id || a._id}>
                        {a.intitule} {a.estActive ? '⭐' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.annee_universitaire_id}>
                  <InputLabel>Filière *</InputLabel>
                  <Select
                    value={formData.filiere_id}
                    onChange={(e) => setFormData({ ...formData, filiere_id: e.target.value, niveau_id: '', periode_id: '', module_id: '' })}
                    label="Filière *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {formFilteredFilieres.map((f) => (
                      <MenuItem key={f.filiere_id} value={f.filiere_id}>
                        {f.code_filiere || f.libelle} - {f.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.filiere_id}>
                  <InputLabel>Niveau *</InputLabel>
                  <Select
                    value={formData.niveau_id}
                    onChange={(e) => setFormData({ ...formData, niveau_id: e.target.value, periode_id: '', module_id: '' })}
                    label="Niveau *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {formFilteredNiveaux.map((n) => (
                      <MenuItem key={n.niveau_id} value={n.niveau_id}>
                        {n.intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.niveau_id}>
                  <InputLabel>Période *</InputLabel>
                  <Select
                    value={formData.periode_id}
                    onChange={(e) => setFormData({ ...formData, periode_id: e.target.value, module_id: '' })}
                    label="Période *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {formFilteredPeriodes.map((p) => (
                      <MenuItem key={p._id} value={p._id}>
                        {p.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" disabled={!formData.periode_id}>
                  <InputLabel>Module *</InputLabel>
                  <Select
                    value={formData.module_id}
                    onChange={(e) => {
                      const id = e.target.value;
                      // L annee, la filiere, le niveau et la periode ne sont PAS des choix
                      // libres : ils sont portes par le module. On les aligne sur lui plutot que
                      // de laisser quatre champs saisir a la main, quatre valeurs pouvant
                      // diverger et rendre la matiere invisible dans l emploi du temps.
                      const mod = getModule(id);
                      setFormData({
                        ...formData,
                        module_id: id,
                        ...(mod ? {
                          annee_universitaire_id: mod.annee_universitaire_id || formData.annee_universitaire_id,
                          filiere_id: mod.filiere_id || formData.filiere_id,
                          niveau_id: mod.niveau_id || formData.niveau_id,
                          periode_id: mod.periode_id || formData.periode_id
                        } : {})
                      });
                    }}
                    label="Module *"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    {formFilteredModules.map((m) => (
                      <MenuItem key={m._id} value={m._id}>
                        {m.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Crédits"
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Coefficient"
                  type="number"
                  value={formData.coefficient}
                  onChange={(e) => setFormData({ ...formData, coefficient: parseFloat(e.target.value) || 1 })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enseignant ID"
                  value={formData.enseignant_id}
                  onChange={(e) => setFormData({ ...formData, enseignant_id: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Volume Horaire</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Cours"
                      type="number"
                      value={formData.volumeHoraire.cours}
                      onChange={(e) => setFormData({
                        ...formData,
                        volumeHoraire: { ...formData.volumeHoraire, cours: parseInt(e.target.value) || 0 }
                      })}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="TD"
                      type="number"
                      value={formData.volumeHoraire.td}
                      onChange={(e) => setFormData({
                        ...formData,
                        volumeHoraire: { ...formData.volumeHoraire, td: parseInt(e.target.value) || 0 }
                      })}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="TP"
                      type="number"
                      value={formData.volumeHoraire.tp}
                      onChange={(e) => setFormData({
                        ...formData,
                        volumeHoraire: { ...formData.volumeHoraire, tp: parseInt(e.target.value) || 0 }
                      })}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextareaAutosize
                  minRows={3}
                  placeholder="Description..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '8px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    resize: 'vertical'
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.estActif ? 'actif' : 'inactif'}
                    onChange={(e) => setFormData({ ...formData, estActif: e.target.value === 'actif' })}
                    label="Statut"
                  >
                    <MenuItem value="actif">Actif</MenuItem>
                    <MenuItem value="inactif">Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} sx={{ bgcolor: '#667eea' }}>
            {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Matieres;


